/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {MouseEvent} from 'react';
import {Moment} from 'moment-timezone';
import {FilterItems, Filters} from '../types/types';
import {FilterState} from './useFilterState';

// defines the type definitions for augmentor ...
export type AugmentorType = (value: string | object, index: number, array: (string | object)[]) => unknown;

// defines the type definitions for filter options ...
export type FilterParamsProps = {
  options?: (string | object)[];
  selections?: (string | object)[];
  augmentOptions?: AugmentorType;
  augmentSelections?: AugmentorType;
  optionsKey?: string;
  selectionsKey?: string;
  loading?: boolean;
  hasError?: boolean;
  selectedGroups?: (string | object)[];
  selectedAssets?: (string | object)[];
  selectedDevices?: (string | object)[];
  fromDate?: Moment;
  toDate?: Moment;
  selectedRadio?: string;
};

// defines the useFilterSubmit prop type definitions ...
export type UseFilterSubmitProps = {
  filterId: string;
  filterState: FilterState;
  setFilterState: (val: FilterState) => void;
  filterParams: FilterParamsProps;
  onApply?: (val: (string | object)[] | object) => void;
};

export const getItems = (
  options: (string | object)[],
  augmentor: AugmentorType,
  optionsKey: string
) => {
  if (options?.length > 0) {
    if (optionsKey) {
      return options?.map(augmentor || (option => option?.[optionsKey]));
    } else {
      return options;
    }
  } else {
    return [];
  }
};

/**
 * @useFilterSubmit
 *
 * Provides a reusable hook which allows for automating the process of applying a
 * filter.
 *
 * ```typescript
 *
 *   import {useFilterSubmit} from '@ion/dls/src/filters';
 *
 *   const onSubmit = useFilterSubmit({
 *     filterState,
 *     setFilterState,
 *     filterParams,
 *     onSubmit
 *   });
 *
 * ```
 *
 */
export const useFilterSubmit = ({
  filterId,
  filterState,
  setFilterState,
  filterParams,
  onApply
}: UseFilterSubmitProps
) => {
  return function(e?: MouseEvent<HTMLButtonElement>) {
    const {
      options,
      selections,
      augmentOptions,
      augmentSelections,
      optionsKey,
      selectionsKey,
      loading,
      hasError,
      selectedAssets,
      selectedGroups,
      selectedDevices
    } = filterParams || {};
    if (filterId === Filters.date) {
      setFilterState({
        [filterId] : {
          startDate : filterParams.fromDate,
          endDate   : filterParams.toDate,
          dateValue : filterParams.selectedRadio
        }
      });
      onApply?.(filterParams);
      return;
    } else if (filterId !== Filters.groups) {
      const items         = getItems(options, augmentOptions, optionsKey);
      const selectedItems = getItems(selections, augmentSelections, selectionsKey);
      const filterItems: FilterItems = Object.assign({},
        filterState?.[filterId],
        {loading, items, selected: selectedItems, error: hasError}
      );
      setFilterState({[filterId]: {...filterItems}});
      onApply?.(filterItems?.selected);
      return;
    }
    onApply?.({selectedAssets, selectedGroups, selectedDevices});
  };
};

export default useFilterSubmit;
